import React from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * F — the prominent warning for a comparable that does not agree with the others.
 *
 * Sits directly under the verdict, not in the comps section: the risk is that the user
 * reads BUY and never notices that one comp pulled the number. Each outlier is named
 * with its price, how far it sits from the other comps, and the direction — a low comp
 * drags the ARV down, a high one inflates it — and the button takes them to the comps.
 */
const UserCompOutlierAlert = ({ assessment, lang = 'en', onReview }) => {
  const outliers = assessment?.outliers || [];
  if (!outliers.length) return null;
  const anyAbove = outliers.some((o) => o.direction === 'above');
  const anyBelow = outliers.some((o) => o.direction === 'below');
  const title = lang === 'es'
    ? (outliers.length === 1
        ? 'Un comparable no encaja con los demás'
        : `${outliers.length} comparables no encajan con los demás`)
    : (outliers.length === 1
        ? 'One comparable does not fit the others'
        : `${outliers.length} comparables do not fit the others`);
  const effect = lang === 'es'
    ? (anyAbove && anyBelow ? 'Tu ARV puede estar distorsionado'
      : anyAbove ? 'Tu ARV puede estar inflado' : 'Tu ARV puede estar más bajo de lo real')
    : (anyAbove && anyBelow ? 'Your ARV may be distorted'
      : anyAbove ? 'Your ARV may be inflated' : 'Your ARV may be lower than it should be');
  const tier = assessment?.tier_score;
  return (
    <div
      className="mb-4 sm:mb-6 rounded-xl border-2 border-red-500 bg-red-50 p-4 sm:p-5 flex items-start gap-3 shadow-md"
      role="alert"
      data-testid="user-comp-outlier-alert"
    >
      <AlertTriangle className="w-7 h-7 text-red-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <h3 className="text-base sm:text-lg font-bold text-red-900 leading-snug">
          {title} — {effect}
        </h3>
        <ul className="mt-2 space-y-1.5">
          {outliers.map((o) => (
            <li key={o.address} className="text-sm sm:text-base text-red-900 font-medium" data-testid="user-comp-outlier">
              {lang === 'es' ? (o.message_es || o.message) : (o.message_en || o.message)}
            </li>
          ))}
        </ul>
        <p className="mt-2 text-xs sm:text-sm text-red-800">
          {lang === 'es'
            ? `Por eso la confianza del ARV baja a ${tier === 55 ? 'MEDIA' : tier === 70 ? 'MEDIA-ALTA' : tier === 35 ? 'BAJA' : 'ALTA'} (${tier}). Revisa ese comparable antes de decidir.`
            : `That is why the ARV confidence drops to ${tier === 55 ? 'MEDIUM' : tier === 70 ? 'MEDIUM-HIGH' : tier === 35 ? 'LOW' : 'HIGH'} (${tier}). Check that comparable before you decide.`}
        </p>
        {onReview && (
          <button
            type="button"
            onClick={onReview}
            className="mt-3 px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors"
            data-testid="user-comp-outlier-review"
          >
            {lang === 'es' ? 'Revisar comparables' : 'Review comparables'}
          </button>
        )}
      </div>
    </div>
  );
};

export default UserCompOutlierAlert;
